package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.DeleteSweep
import androidx.compose.material.icons.outlined.FileDownload
import androidx.compose.material.icons.outlined.History
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminStaffAuditEvent
import org.gymly.model.AdminStaffAuditFilterGroup
import org.gymly.model.AuditEntry
import org.gymly.theme.AdminColors
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.downloadBytes
import org.gymly.util.userMessage
import kotlin.time.Instant

@Composable
internal fun ColumnScope.SettingsAuditPanel(dateFilterDays: Int?, compact: Boolean) {
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var events by remember { mutableStateOf<List<AuditEntry>>(emptyList()) }
    var query by remember { mutableStateOf("") }
    var groupFilter by remember { mutableStateOf<AdminStaffAuditFilterGroup?>(null) }
    var typeMenu by remember { mutableStateOf(false) }
    var selected by remember { mutableStateOf<AuditEntry?>(null) }
    var confirmPurge by remember { mutableStateOf(false) }
    var busyAction by remember { mutableStateOf(false) }
    var actionMessage by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                val staffEvents = AdminRepo.api.listStaffAuditEvents(days = dateFilterDays).map { AuditEntry.Staff(it) }
                // TODO(IA): suspendido. El backend no compila los controladores de IA (Kotlin) y
                // /api/admin/ai-config/audit-logs responde 404. Reactivar junto con el módulo de IA.
                // val aiLogs = AdminRepo.api.listAIAuditLogs().map { AuditEntry.AI(it) }
                // events = (staffEvents + aiLogs).sortedByDescending { it.occurredAt }
                events = staffEvents.sortedByDescending { it.occurredAt }
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar la auditoría.")
                events = emptyList()
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(dateFilterDays) {
        loading = true
        error = null
        try {
            val staffEvents = AdminRepo.api.listStaffAuditEvents(days = dateFilterDays).map { AuditEntry.Staff(it) }
            // TODO(IA): ver comentario arriba. Reactivar al retomar el módulo de IA.
            // val aiLogs = AdminRepo.api.listAIAuditLogs().map { AuditEntry.AI(it) }
            // events = (staffEvents + aiLogs).sortedByDescending { it.occurredAt }
            events = staffEvents.sortedByDescending { it.occurredAt }
        } catch (e: Exception) {
            error = e.userMessage("No se pudo cargar la auditoría.")
            events = emptyList()
        } finally {
            loading = false
        }
    }

    val filtered = remember(events, query, groupFilter) {
        val q = query.trim().lowercase()
        events.filter { e ->
            val matchesQuery = q.isEmpty() ||
                e.occurredAt.lowercase().contains(q) ||
                when (e) {
                    is AuditEntry.Staff -> {
                        e.event.staffName.lowercase().contains(q) ||
                        (e.event.staffEmail?.lowercase()?.contains(q) == true) ||
                        (e.event.moduleLabel?.lowercase()?.contains(q) == true) ||
                        e.event.eventLabel.lowercase().contains(q) ||
                        e.event.eventType.labelEs().lowercase().contains(q) ||
                        e.event.displayDetail().lowercase().contains(q) ||
                        (e.event.entityId?.lowercase()?.contains(q) == true)
                    }
                    is AuditEntry.AI -> {
                        e.log.agentName.lowercase().contains(q) ||
                        e.log.featureKey.lowercase().contains(q) ||
                        e.log.decisionSummary.lowercase().contains(q) ||
                        e.log.providerUsed.lowercase().contains(q)
                    }
                }
            val matchesGroup = groupFilter == null ||
                when (e) {
                    is AuditEntry.Staff -> e.event.eventType.filterGroup() == groupFilter
                    is AuditEntry.AI -> groupFilter == AdminStaffAuditFilterGroup.CONFIG // AI logs under Config
                }
            matchesQuery && matchesGroup
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "occurredAt",
        initialDir = AdminSortDir.Desc,
        resetKey = listOf(query, groupFilter, dateFilterDays),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "staff" -> { e -> when (e) { is AuditEntry.Staff -> e.event.staffName.lowercase() ; is AuditEntry.AI -> e.log.agentName.lowercase() } }
                "event" -> { e -> when (e) { is AuditEntry.Staff -> e.event.eventLabel.lowercase() ; is AuditEntry.AI -> e.log.featureKey.lowercase() } }
                "module" -> { e -> when (e) { is AuditEntry.Staff -> e.event.moduleLabel?.lowercase() ; is AuditEntry.AI -> "IA" } }
                "duration" -> { e -> when (e) { is AuditEntry.Staff -> e.event.durationSeconds ; is AuditEntry.AI -> null } }
                "detail" -> { e -> when (e) { is AuditEntry.Staff -> e.event.displayDetail().lowercase() ; is AuditEntry.AI -> e.log.decisionSummary.lowercase() } }
                else -> { e -> e.occurredAt }
            }
        }
    }

    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Outlined.History, null, tint = AdminColors.Primary)
            Text(
                "Historial de actividad",
                fontWeight = FontWeight.Bold,
                fontSize = 16.sp,
                modifier = Modifier.padding(start = 8.dp),
            )
        }
        Spacer(Modifier.height(6.dp))
        Text(
            "Accesos, permanencia en módulos y acciones de negocio. Retención automática: 90 días.",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Spacer(Modifier.height(14.dp))
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            OutlinedTextField(
                value = query,
                onValueChange = { query = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = { Text("Buscar usuario, módulo o acción...", color = AdminColors.TextMuted) },
                leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box {
                    OutlinedButton(onClick = { typeMenu = true }, shape = RoundedCornerShape(10.dp)) {
                        Text(groupFilter?.labelEs ?: "Todos los eventos", color = AdminColors.Text)
                    }
                    DropdownMenu(expanded = typeMenu, onDismissRequest = { typeMenu = false }) {
                        DropdownMenuItem(
                            text = { Text("Todos los eventos") },
                            onClick = {
                                groupFilter = null
                                typeMenu = false
                            },
                        )
                        AdminStaffAuditFilterGroup.entries.forEach { g ->
                            DropdownMenuItem(
                                text = { Text(g.labelEs) },
                                onClick = {
                                    groupFilter = g
                                    typeMenu = false
                                },
                            )
                        }
                    }
                }
                OutlinedButton(
                    onClick = {
                        if (busyAction) return@OutlinedButton
                        busyAction = true
                        actionMessage = null
                        scope.launch {
                            try {
                                val bytes = AdminRepo.api.exportStaffAuditCsv(days = dateFilterDays)
                                downloadBytes(
                                    "historial-actividad.csv",
                                    "text/csv",
                                    bytes,
                                )
                                actionMessage = "CSV descargado (${filtered.size} en vista; exporta el periodo del filtro de fechas)."
                            } catch (e: Exception) {
                                actionMessage = e.userMessage("No se pudo exportar el CSV.")
                            } finally {
                                busyAction = false
                            }
                        }
                    },
                    enabled = !busyAction && !loading,
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Icon(Icons.Outlined.FileDownload, null, tint = AdminColors.Primary)
                    Spacer(Modifier.width(6.dp))
                    Text("Exportar CSV", color = AdminColors.Text)
                }
                OutlinedButton(
                    onClick = { confirmPurge = true },
                    enabled = !busyAction && !loading,
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Icon(Icons.Outlined.DeleteSweep, null, tint = AdminColors.Error)
                    Spacer(Modifier.width(6.dp))
                    Text("Limpiar accesos >90d", color = AdminColors.Text)
                }
            }
            actionMessage?.let {
                Text(it, color = AdminColors.TextMuted, fontSize = 12.sp)
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            error != null -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text(error!!, color = AdminColors.Error, fontSize = 13.sp)
            }
            filtered.isEmpty() -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("Sin eventos en el periodo seleccionado.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    pageItems.forEach { event ->
                        AuditEventCard(event, onClick = { selected = event })
                    }
                }
                AdminListPaginationBar(
                    totalItems = filtered.size,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                AuditEventsTable(
                    pageItems,
                    listPaging = listPaging,
                    onOpen = { selected = it },
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = filtered.size,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }

    selected?.let { entry ->
        when (entry) {
            is AuditEntry.Staff -> AuditEventDetailDialog(event = entry.event, onDismiss = { selected = null })
            is AuditEntry.AI -> AIEventDetailDialog(log = entry.log, onDismiss = { selected = null })
        }
    }

    if (confirmPurge) {
        AlertDialog(
            onDismissRequest = { if (!busyAction) confirmPurge = false },
            title = { Text("Limpiar accesos") },
            text = {
                Text(
                    "Se eliminarán solo eventos de acceso (inicios de sesión, cierres y navegación) con más de 90 días. " +
                        "Los cobros, ventas, órdenes y aprobaciones no se borran. Esta acción no se puede deshacer. " +
                        "También corre automáticamente cada día a las 03:15.",
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        if (busyAction) return@TextButton
                        busyAction = true
                        scope.launch {
                            try {
                                val (removed, msg) = AdminRepo.api.purgeStaffAudit(90)
                                actionMessage = msg.ifBlank { "Eliminados $removed eventos." }
                                confirmPurge = false
                                reload()
                            } catch (e: Exception) {
                                actionMessage = e.userMessage("No se pudo limpiar el historial.")
                                confirmPurge = false
                            } finally {
                                busyAction = false
                            }
                        }
                    },
                ) {
                    Text("Eliminar", color = AdminColors.Error)
                }
            },
            dismissButton = {
                TextButton(onClick = { confirmPurge = false }, enabled = !busyAction) {
                    Text("Cancelar")
                }
            },
        )
    }
}

@Composable
private fun AuditEventsTable(
    events: List<AuditEntry>,
    listPaging: AdminListPaging,
    onOpen: (AuditEntry) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminSortableHeader("Fecha / hora", "occurredAt", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
            AdminSortableHeader("Usuario / Agente", "staff", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
            AdminSortableHeader("Evento / Feature", "event", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Módulo", "module", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Detalle", "detail", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(
            Modifier.weight(1f).fillMaxWidth(),
        ) {
            events.forEach { event ->
                AuditEventRow(event, onClick = { onOpen(event) })
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun AuditEventRow(event: AuditEntry, onClick: () -> Unit) {
    val user = when (event) {
        is AuditEntry.Staff -> event.event.staffName
        is AuditEntry.AI -> event.log.agentName
    }
    val eventLabel = when (event) {
        is AuditEntry.Staff -> event.event.eventLabel
        is AuditEntry.AI -> event.log.featureKey
    }
    val module = when (event) {
        is AuditEntry.Staff -> event.event.moduleLabel ?: "—"
        is AuditEntry.AI -> "IA"
    }
    val detail = when (event) {
        is AuditEntry.Staff -> event.event.displayDetail()
        is AuditEntry.AI -> event.log.decisionSummary
    }
    Row(
        Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 10.dp, horizontal = 4.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Text(formatAuditTimestamp(event.occurredAt), Modifier.weight(1.2f), fontSize = 12.sp, color = AdminColors.Text)
        Text(user, Modifier.weight(1.2f), fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
        Text(eventLabel, Modifier.weight(1f), fontSize = 12.sp, color = AdminColors.Text)
        Text(module, Modifier.weight(1f), fontSize = 12.sp, color = AdminColors.Text)
        Text(detail, Modifier.weight(1.2f), fontSize = 12.sp, color = AdminColors.TextMuted, maxLines = 2)
    }
}

@Composable
private fun AuditEventCard(event: AuditEntry, onClick: () -> Unit) {
    when (event) {
        is AuditEntry.Staff -> {
            val e = event.event
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color(0xFFF8FAFC))
                    .clickable(onClick = onClick)
                    .padding(14.dp),
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text(e.eventLabel, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                    Text(formatAuditTimestamp(e.occurredAt), color = AdminColors.TextMuted, fontSize = 12.sp)
                }
                Spacer(Modifier.height(6.dp))
                Text(e.staffName, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                e.staffEmail?.let { Text(it, color = AdminColors.TextMuted, fontSize = 12.sp) }
                Spacer(Modifier.height(8.dp))
                Text("Módulo: ${e.moduleLabel ?: "—"}", fontSize = 12.sp)
                Text("Duración: ${e.displayDuration()}", fontSize = 12.sp)
                Text(e.displayDetail(), color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
            }
        }
        is AuditEntry.AI -> {
            val log = event.log
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color(0xFFF8FAFC))
                    .clickable(onClick = onClick)
                    .padding(14.dp),
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text(log.featureKey, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                    Text(formatAuditTimestamp(log.occurredAt), color = AdminColors.TextMuted, fontSize = 12.sp)
                }
                Spacer(Modifier.height(6.dp))
                Text(log.agentName, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                Text("Proveedor: ${log.providerUsed}", fontSize = 12.sp)
                Spacer(Modifier.height(8.dp))
                Text("Feature: ${log.featureKey}", fontSize = 12.sp)
                Text("Confianza: ${log.confidenceScore}", fontSize = 12.sp)
                Text(log.decisionSummary, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
            }
        }
    }
}

@Composable
private fun AuditEventDetailDialog(event: AdminStaffAuditEvent, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 520.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text(event.eventLabel, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Text(formatAuditTimestamp(event.occurredAt), color = AdminColors.TextMuted, fontSize = 12.sp)
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            DetailLine("Usuario", event.staffName)
            event.staffEmail?.let { DetailLine("Correo", it) }
            DetailLine("Módulo", event.moduleLabel ?: "—")
            DetailLine("Duración", event.displayDuration())
            DetailLine("Detalle", event.displayDetail())
            if (!event.entityType.isNullOrBlank() || !event.entityId.isNullOrBlank()) {
                DetailLine(
                    "Registro",
                    listOfNotNull(event.entityLabelEs(), event.entityId).joinToString(" · "),
                )
            }
            event.sessionId?.let { DetailLine("Sesión", it) }
            DetailLine("Id evento", event.id)
            Spacer(Modifier.height(8.dp))
            Text(
                "Tip: la duración solo aplica a “Módulo visitado”. La referencia de registro se llena en acciones nuevas (p. ej. clientes).",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss) {
                    Text("Cerrar", color = AdminColors.Primary)
                }
            }
        }
    }
}

@Composable
private fun DetailLine(label: String, value: String) {
    Column(Modifier.fillMaxWidth()) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, fontSize = 13.sp, fontWeight = FontWeight.Medium)
    }
}

private fun formatAuditTimestamp(raw: String): String {
    val instant = runCatching { Instant.parse(raw) }.getOrNull() ?: return raw.take(16).replace('T', ' ')
    return instant.toString().take(16).replace('T', ' ')
}

@Composable
private fun AIEventDetailDialog(log: org.gymly.ai.model.AIAuditLog, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 520.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text("Detalle IA: ${log.featureKey}", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Text(log.occurredAt.take(16).replace('T', ' '), color = AdminColors.TextMuted, fontSize = 12.sp)
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            DetailLine("Agente", log.agentName)
            DetailLine("Proveedor", log.providerUsed)
            DetailLine("Confianza", log.confidenceScore.toString())
            DetailLine("Resumen", log.decisionSummary)
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss) { Text("Cerrar", color = AdminColors.Primary) }
            }
        }
    }
}
